import './menuList.scss';

import { Box, ButtonGroup, Flex, For, Separator, Text, VStack } from '@chakra-ui/react';
import { MenuSubItem } from '@components/ui/menuSubItem/MenuSubItem';
import { flip, offset, shift, useFloating } from '@floating-ui/react';
import { findMenuItem } from '@utils/common.utils';
import { isGroup, isSeparator } from '@utils/menu.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MenuItemOption, MenuItemOptionIcons, MenuItems } from '@/types/menu.types';

function EmptyOptionFallback({ label }: Readonly<{ label: string }>) {
  const { formatMessage } = useIntl();

  return (
    <Flex width="100%" backgroundColor="neutral.800" padding={1} paddingLeft={2} alignItems="center" cursor="default">
      <Text>
        <FormattedMessage
          id="global.empty"
          values={{ item: formatMessage({ id: label }).toLowerCase().replace(/s$/, '') }}
        />
      </Text>
    </Flex>
  );
}

type ControlledProps =
  | {
      selectedOptions: string[];
      handleClick?: (option: string, value: MenuItemOption | MenuItemOptionIcons | undefined, depth: number) => void;
    }
  | {
      selectedOptions?: never;
      handleClick?: never;
    };

type MenuListProps = {
  fullMenuItems?: MenuItems;
  menuItems: MenuItems;
  depth?: number;
  parentLabel?: string;
  buttonWidth?: string;
  resetNavigation?: boolean;
  hideBorder?: boolean;
} & ControlledProps;

function MenuList({
  menuItems,
  fullMenuItems = menuItems,
  depth = 0,
  parentLabel,
  buttonWidth,
  resetNavigation,
  selectedOptions,
  handleClick,
  hideBorder = false,
}: Readonly<MenuListProps>) {
  const { formatMessage } = useIntl();

  const [localSelectedOptions, setLocalSelectedOptions] = useState<string[]>(selectedOptions ?? []);
  const localHandleClick = (option: string, value: MenuItemOption | undefined, depth: number) => {
    const temp = structuredClone(localSelectedOptions).slice(0, depth + 1);
    // click on same place => revert navigation
    if (localSelectedOptions[depth] === option) {
      setLocalSelectedOptions(temp.slice(0, depth));
    } else {
      // else forward navigation
      setLocalSelectedOptions(temp.toSpliced(depth, 1, option));
      // trigger all potential action linked to menu btn
      if (value?.onAction) {
        value.onAction();
        //resetNavigation
        resetNavigation && setLocalSelectedOptions(temp.slice(0, depth));
      }
    }
  };

  const optionsToUse = selectedOptions ?? localSelectedOptions;

  const activeKey = optionsToUse[depth];

  const { refs, floatingStyles } = useFloating({
    placement: 'right-start',
    middleware: [
      offset(({ placement }) => {
        const isEnd = placement.endsWith('end');
        return {
          mainAxis: 0,
          crossAxis: isEnd ? 2 : -2,
        };
      }),
      flip(),
      shift(),
    ],
  });

  const renderMenuItem = (key: string, value: MenuItemOption, menuItems: MenuItems | undefined) => {
    const isSelectedOption = value.isSelected ?? optionsToUse[depth] === key;
    const { subMenuItems, label, isDisabled, isActive, isLoading, shouldNotTranslate } = value;

    const labelToDisplay = typeof label === 'string' && !shouldNotTranslate ? formatMessage({ id: label }) : label;
    const functionToUse = handleClick ?? localHandleClick;

    return (
      <Box key={key} position="relative" ref={key === activeKey ? refs.setReference : undefined}>
        <MenuSubItem
          label={labelToDisplay}
          isSelected={isSelectedOption}
          isDisabled={isDisabled}
          isActive={isActive}
          depth={depth}
          hasChildren={!!subMenuItems}
          onClick={() => functionToUse(key, value, depth)}
          onHover={() => {
            const currentLastSelectedChildren = findMenuItem(fullMenuItems, optionsToUse[optionsToUse.length - 1]);
            if (currentLastSelectedChildren === undefined || currentLastSelectedChildren.subMenuItems !== undefined) {
              if (subMenuItems !== undefined && !isSelectedOption) {
                functionToUse(key, value, depth);
              } else if (!subMenuItems && menuItems && Object.keys(menuItems).includes(optionsToUse[depth])) {
                functionToUse(selectedOptions ? selectedOptions[depth] : localSelectedOptions[depth], undefined, depth);
              }
            }
          }}
          isLoading={isLoading}
        />
        {key === optionsToUse[depth] && subMenuItems && (
          <VStack ref={refs.setFloating} style={floatingStyles} zIndex={10} gap={0}>
            <MenuList
              fullMenuItems={fullMenuItems}
              menuItems={subMenuItems}
              depth={depth + 1}
              buttonWidth={buttonWidth}
              selectedOptions={optionsToUse}
              handleClick={functionToUse}
            />
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <VStack
      gap={0}
      border={hideBorder ? undefined : '2px solid'}
      borderColor="neutral.black"
      width="min-content"
      userSelect="none"
    >
      {parentLabel && (
        <Flex
          width="100%"
          backgroundColor="neutral.black"
          padding={1}
          paddingLeft={2}
          alignItems="center"
          cursor="default"
        >
          <Text textStyle="screenSm" fontWeight="medium" color="skyBlue">
            {formatMessage({ id: parentLabel }).toUpperCase()}
          </Text>
        </Flex>
      )}
      <ButtonGroup
        display="flex"
        flexDir="column"
        gap={0}
        width={buttonWidth ?? '100%'}
        alignItems="stretch"
        justify="flex-start"
      >
        <For each={Object.entries(menuItems)}>
          {([key, value], index) => {
            if (isGroup(value)) {
              return (
                <Box key={key}>
                  <Flex
                    height="32px"
                    width="100%"
                    paddingRight={1}
                    paddingLeft={2}
                    paddingY={0.5}
                    alignItems="center"
                    cursor="default"
                    backgroundColor="neutral.900"
                  >
                    <Text color="neutral.300">
                      <FormattedMessage id={value.label} />
                    </Text>
                  </Flex>
                  <ButtonGroup
                    display="flex"
                    flexDir="column"
                    gap={0}
                    width={buttonWidth ?? '100%'}
                    alignItems="stretch"
                    justify="flex-start"
                  >
                    <For each={Object.entries(value.items)} fallback={<EmptyOptionFallback label={value.label} />}>
                      {([key, subValue]) => renderMenuItem(key, subValue, subValue.subMenuItems)}
                    </For>
                  </ButtonGroup>
                  <Separator key={`${value.type}-${index}`} borderColor="neutral.black" size="md" />
                </Box>
              );
            } else if (isSeparator(value)) {
              return <Separator key={`${value.type}-${index}`} borderColor="neutral.black" size="md" />;
            } else if (!isSeparator(value) && !value.isHidden) {
              return renderMenuItem(key, value, value.subMenuItems);
            }
          }}
        </For>
      </ButtonGroup>
    </VStack>
  );
}

export default MenuList;
